import { Component, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { DocumentoPublicoService } from '@shared/services/apis/orquestrador-vendas/documento-publico.service';
import { GetDocumentoPublicoResponse } from '@shared/services/responses/orquestrador-vendas/get-documento-publico.response';
import { CpfCnpjPipe } from '@shared/pipes/cpf-cnpj.pipe';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { VendaFolhaComponent } from '@shared/components/venda-folha/venda-folha.component';
import { LayoutImpressaoVendaEnum } from '@shared/enums/vendas/layout-impressao-venda.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';

type PortalState = 'loading' | 'indisponivel' | 'view';

// Página PÚBLICA (fora do shell autenticado) onde o cliente visualiza o orçamento ou o pedido de venda
// compartilhado por link: a mesma folha da impressão (`app-venda-folha`), com as configurações de impressão
// da empresa, o seletor de layout do pedido e o "Gerar PDF". Credencial = GUID do link; só leitura. Imprimir
// pelo link não grava ocorrência.
@Component({
  selector: 'app-portal-venda',
  standalone: true,
  imports: [AlertCardComponent, EmptyStateComponent, VendaFolhaComponent, MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule],
  templateUrl: './portal-venda.component.html',
  styleUrls: ['./portal-venda.component.css'],
})
export class PortalVendaComponent implements OnInit {
  public readonly layouts: number[] = [
    LayoutImpressaoVendaEnum.Padrao,
    LayoutImpressaoVendaEnum.Detalhado,
    LayoutImpressaoVendaEnum.Recibo,
  ];

  public state: PortalState = 'loading';
  public resposta: GetDocumentoPublicoResponse | null = null;
  public mensagemErro = '';
  public layout: number = LayoutImpressaoVendaEnum.Padrao;
  public isImprimindo = false;

  @ViewChild(VendaFolhaComponent) private folha?: VendaFolhaComponent;

  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    private route: ActivatedRoute,
    private documentoService: DocumentoPublicoService,
    private alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    // No SSR renderizamos apenas o "loading": a chamada HTTP anônima falharia no
    // servidor e assaria o estado "indisponível" no HTML antes da hidratação.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!token) {
      this.state = 'indisponivel';
      return;
    }

    this.documentoService.obter(token).subscribe({
      next: res => {
        this.resposta = res;
        if (res.isError) {
          this.mensagemErro = res.errorDescription || 'Não foi possível carregar o documento.';
          this.state = 'indisponivel';
          return;
        }
        this.state = res.disponivel && res.folha ? 'view' : 'indisponivel';
      },
      error: (erro: HttpErrorResponse) => {
        this.mensagemErro = erro?.status === 429
          ? 'Muitas tentativas. Aguarde um minuto e recarregue.'
          : 'Não foi possível carregar o documento.';
        this.state = 'indisponivel';
      },
    });
  }

  public get tituloIndisponivel(): string {
    return this.mensagemErro ? 'Não foi possível abrir o documento' : 'Link indisponível';
  }

  /** Link vencido diz com quem falar (o vendedor ou, sem ele, a empresa); revogado ou inexistente, nada. */
  public get mensagemIndisponivel(): string {
    if (this.mensagemErro) return this.mensagemErro;

    const vendedor = this.resposta?.contatoVendedor;
    if (vendedor) {
      return `Este link não está mais disponível. Fale com ${vendedor.nome}${vendedor.email ? ` (${vendedor.email})` : ''}.`;
    }

    const empresa = this.resposta?.contatoEmpresa;
    if (empresa) {
      const cnpj = empresa.cnpj ? ` (CNPJ ${new CpfCnpjPipe().transform(empresa.cnpj)})` : '';
      return `Este link não está mais disponível. Fale com ${empresa.nome}${cnpj}.`;
    }

    return 'Este link não está disponível.';
  }

  public get isPedido(): boolean {
    return this.resposta?.folha?.documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
  }

  public get tituloDocumento(): string {
    const documento = this.resposta?.folha?.documento;
    if (!documento) return '';

    // Link de modelo nunca abre (indisponível no servidor): o número sai puro.
    const tipo = this.isPedido ? 'Pedido de Venda' : 'Orçamento';
    return `${tipo} Nº ${documento.numero}`;
  }

  public alterarLayout(layout: number): void {
    this.layout = layout;
  }

  public rotuloLayout(layout: number): string {
    if (layout === LayoutImpressaoVendaEnum.Detalhado) return 'detalhado';
    if (layout === LayoutImpressaoVendaEnum.Recibo) return 'recibo';
    return 'padrão';
  }

  /** Abre a janela de impressão do navegador, onde o cliente imprime ou salva em PDF. */
  public imprimir(): void {
    if (!this.folha || this.isImprimindo) return;

    this.isImprimindo = true;
    this.folha.imprimir().then(abriu => {
      this.isImprimindo = false;

      if (!abriu) {
        this.alertService.show('Aviso', 'Libere as janelas pop-up do navegador para gerar o PDF.', AlertType.warning);
      }
    });
  }
}
